// Utility functions for data export functionality
import { AUTISM_INFORMANT_FORM_KEYS } from '../../../../constants/adultAutismFormKeys';
import { isPatientFemale } from '../../../../utils/conditionChecks';

const EMPTY_CATEGORY = () => ({
  forms: [],
  count: 0,
  hasData: false,
  statusCounts: { completed: 0, started: 0, not_started: 0, total: 0 },
});

/**
 * Analyzes patient data to categorize forms for export.
 *
 * Returns four category buckets: patientHistory, patientScales,
 * informantScales, and informantHistory. The informantHistory bucket is
 * populated only for modular patients (Autism and CYP ADHD) — adult ADHD
 * patients always get informantHistory.hasData === false.
 *
 * @param {Object} patientDataModel - The patient's data model
 * @param {Object} informantData - Informant data (if available)
 * @returns {Object} - Categorized form data for export
 */
export const analyzePatientDataForExport = (patientDataModel, informantData = {}) => {
  const result = {
    patientHistory: EMPTY_CATEGORY(),
    patientScales: EMPTY_CATEGORY(),
    informantScales: EMPTY_CATEGORY(),
    informantHistory: EMPTY_CATEGORY(),
    totalForms: 0,
  };

  if (!patientDataModel) return result;

  // Get form data and metadata
  const formData = patientDataModel.formData || {};
  const preAppointmentSections = patientDataModel.dataModel?.['1_preAppointmentInformation']?.sections || {};

  // Find Patient History section
  const patientHistorySection = Object.values(preAppointmentSections)
    .find(section => section.internalName === "Patient History");

  // Find Patient Scales section  
  const patientScalesSection = Object.values(preAppointmentSections)
    .find(section => section.internalName === "Patient Scales");

  // Find Informant Scales section
  const informantScalesSection = Object.values(preAppointmentSections)
    .find(section => section.internalName === "Informant Scales");

  // Process Patient History forms
  if (patientHistorySection?.domains) {
    Object.entries(patientHistorySection.domains).forEach(([domainKey, domain]) => {
      const matchingFormKey = Object.keys(formData).find(
        key => formData[key]?.formTitle === domain.displayName
      );
      
      if (matchingFormKey && formData[matchingFormKey]) {
        const form = formData[matchingFormKey];
        
        // Check if this form should be included based on gender
        if (shouldIncludeFormInExport(form, domain.displayName, formData)) {
          const formStatus = analyzeFormStatus(form);
          
          result.patientHistory.forms.push({
            key: matchingFormKey,
            title: domain.displayName,
            internalName: domain.internalName,
            hasResponses: formStatus.hasResponses,
            status: formStatus.status,
            isStarted: formStatus.isStarted,
            isCompleted: formStatus.isCompleted,
            lastUpdated: form.lastUpdated,
            completedAt: form.completedAt,
            startedAt: form.startedAt || form.firstStartedAt,
            progress: form.progress
          });
          
          // Count all forms, not just those with responses
          result.patientHistory.statusCounts[formStatus.status]++;
          result.patientHistory.statusCounts.total++;
          result.patientHistory.count = result.patientHistory.statusCounts.total;
          result.patientHistory.hasData = true; // Always true if form exists
        }
      }
    });
  }

  // Process Patient Scales forms
  if (patientScalesSection?.domains) {
    Object.entries(patientScalesSection.domains).forEach(([domainKey, domain]) => {
      const formKey = domain.varName;
      
      if (formData[formKey]) {
        const form = formData[formKey];
        const formStatus = analyzeFormStatus(form);
        
        result.patientScales.forms.push({
          key: formKey,
          title: domain.displayName,
          internalName: domain.internalName,
          hasResponses: formStatus.hasResponses,
          status: formStatus.status,
          isStarted: formStatus.isStarted,
          isCompleted: formStatus.isCompleted,
          lastUpdated: form.lastUpdated,
          completedAt: form.completedAt,
          startedAt: form.startedAt || form.firstStartedAt,
          progress: form.progress,
          scaleResults: form.scaleResults
        });
        
        // Count all forms, not just those with responses
        result.patientScales.statusCounts[formStatus.status]++;
        result.patientScales.statusCounts.total++;
        result.patientScales.count = result.patientScales.statusCounts.total;
        result.patientScales.hasData = true; // Always true if form exists
      }
    });
  }

  // Process Informant Scales
  if (informantScalesSection?.domains && informantData && Object.keys(informantData).length > 0) {
    Object.entries(informantScalesSection.domains).forEach(([domainKey, domain]) => {
      const scaleKey = domain.varName;
      
      // Check each informant for this scale
      Object.entries(informantData).forEach(([informantId, informant]) => {
        if (informant?.formData?.[scaleKey]) {
          const form = informant.formData[scaleKey];
          const formStatus = analyzeFormStatus(form);
          
          // Get informant details from patient data
          const informantInfo = patientDataModel.informants?.[informantId];
          const informantName = informant.displayName || informant.firstName || informant.email || 'Informant';
          const relationship = informantInfo?.informantRelationshipToPatient || 'Unknown';
          
          result.informantScales.forms.push({
            key: scaleKey,
            informantId,
            title: domain.displayName,
            internalName: domain.internalName,
            informantName,
            relationship,
            hasResponses: formStatus.hasResponses,
            status: formStatus.status,
            isStarted: formStatus.isStarted,
            isCompleted: formStatus.isCompleted,
            lastUpdated: form.lastUpdated,
            completedAt: form.completedAt,
            startedAt: form.startedAt || form.firstStartedAt,
            progress: form.progress,
            scaleResults: form.scaleResults
          });
          
          // Count all forms, not just those with responses
          result.informantScales.statusCounts[formStatus.status]++;
          result.informantScales.statusCounts.total++;
          result.informantScales.count = result.informantScales.statusCounts.total;
          result.informantScales.hasData = true; // Always true if form exists
        }
      });
    });
  }

  // Informant History — modular patients only (Autism and CYP ADHD). Each
  // informant's forms are analyzed for export. Adult ADHD patients do not have
  // informant history, so this stays empty for them.
  // - Autism keeps its fixed whitelist (AUTISM_INFORMANT_FORM_KEYS).
  // - CYP ADHD informants (parent/carer and school representative) are analyzed
  //   on their own assigned form set — history questionnaires plus the SNAP-IV
  //   rating scales — ordered by formNumber, matching the server-side modular
  //   informantHistory export which includes every informant formInstance.
  const isAutism = patientDataModel?.assessmentModule === 'adultAutism';
  const isCypAdhd = patientDataModel?.assessmentModule === 'cypAdhd';

  if ((isAutism || isCypAdhd) && informantData && Object.keys(informantData).length > 0) {
    Object.entries(informantData).forEach(([informantId, informant]) => {
      const infFormData = informant?.formData || {};

      const formKeys = isAutism
        ? AUTISM_INFORMANT_FORM_KEYS
        : Object.keys(infFormData).sort(
          (a, b) => (infFormData[a]?.formNumber || 0) - (infFormData[b]?.formNumber || 0)
        );

      formKeys.forEach((formKey) => {
        if (!infFormData[formKey]) return;

        const form = infFormData[formKey];
        const formStatus = analyzeFormStatus(form);
        const informantName = informant.displayName || informant.firstName || informant.email || 'Informant';
        const informantInfo = patientDataModel.informants?.[informantId];
        const relationship = informantInfo?.informantRelationshipToPatient || 'Unknown';

        result.informantHistory.forms.push({
          key: formKey,
          informantId,
          title: form.formTitle || formKey,
          informantName,
          relationship,
          hasResponses: formStatus.hasResponses,
          status: formStatus.status,
          isStarted: formStatus.isStarted,
          isCompleted: formStatus.isCompleted,
          lastUpdated: form.lastUpdated,
          completedAt: form.completedAt,
          startedAt: form.startedAt || form.firstStartedAt,
          progress: form.progress,
        });

        result.informantHistory.statusCounts[formStatus.status]++;
        result.informantHistory.statusCounts.total++;
        result.informantHistory.count = result.informantHistory.statusCounts.total;
        result.informantHistory.hasData = true;
      });
    });
  }

  result.totalForms = result.patientHistory.count
    + result.patientScales.count
    + result.informantScales.count
    + result.informantHistory.count;

  return result;
};

/**
 * Analyzes form status and response completeness
 * @param {Object} form - Form data object
 * @returns {Object} - Form status analysis
 */
const analyzeFormStatus = (form) => {
  const result = {
    hasResponses: false,
    isStarted: false,
    isCompleted: false,
    status: 'not_started' // 'not_started', 'started', 'completed'
  };

  if (!form || !form.formSubsections) return result;

  // Check if form has been started (has startedAt or firstStartedAt timestamp or progress)
  if (form.startedAt || form.firstStartedAt || form.lastUpdated || 
      (form.progress && form.progress.progressPercentage > 0) || 
      (form.scaleResults && !form.scaleResults.complete)) {
    result.isStarted = true;
  }

  // Check if form is explicitly marked as completed
  if (form.completedAt || 
      (form.progress && form.progress.progressPercentage === 100) || 
      form.scaleResults?.complete === true) {
    result.isCompleted = true;
    result.isStarted = true; // completed implies started
  }

  // Check for actual responses
  for (const [, subsection] of Object.entries(form.formSubsections)) {
    if (!subsection.questions) continue;
    
    for (const [, question] of Object.entries(subsection.questions)) {
      if (!question.subquestions) continue;
      
      for (const [subquestionKey, subquestion] of Object.entries(question.subquestions)) {
        // Skip non-numbered subquestion keys like 'noneOptions'
        if (isNaN(parseInt(subquestionKey))) continue;
        
        const answer = subquestion.answer;
        
        // Check if there's a meaningful answer
        if (answer !== null && answer !== undefined && answer !== '') {
          if (Array.isArray(answer) && answer.length > 0) {
            result.hasResponses = true;
            result.isStarted = true;
          }
          if (typeof answer === 'object' && answer.value) {
            result.hasResponses = true;
            result.isStarted = true;
          }
          if (typeof answer === 'string' && answer.trim() !== '') {
            result.hasResponses = true;
            result.isStarted = true;
          }
          if (typeof answer === 'number') {
            result.hasResponses = true;
            result.isStarted = true;
          }
          if (typeof answer === 'boolean') {
            result.hasResponses = true;
            result.isStarted = true;
          }
        }
      }
    }
  }

  // Determine final status
  if (result.isCompleted) {
    result.status = 'completed';
  } else if (result.isStarted || result.hasResponses) {
    result.status = 'started';
  } else {
    result.status = 'not_started';
  }

  return result;
};


/**
 * Checks if a form should be included in export based on patient gender
 * @param {Object} form - Form data object
 * @param {string} formTitle - The title of the form
 * @param {Object} patientFormData - Patient's form data (for gender checking)
 * @returns {boolean} - True if form should be included
 */
const shouldIncludeFormInExport = (form, formTitle, patientFormData) => {
  // If this is a Women's Health form, only include if patient is female
  if (formTitle === "Women's Health") {
    return isPatientFemale(patientFormData);
  }
  // For all other forms, always include
  return true;
};

/**
 * Filters form subsections based on gender (specifically for Women's Health)
 * @param {Object} form - Form data object
 * @param {Object} patientFormData - Patient's form data (for gender checking)
 * @returns {Object} - Filtered form data
 */
const filterFormSubsectionsForExport = (form, patientFormData) => {
  if (!form || !form.formSubsections) return form;
  
  // Create a copy of the form to avoid modifying original data
  const filteredForm = { ...form };
  filteredForm.formSubsections = { ...form.formSubsections };
  
  // Check if we need to filter Women's Health subsection
  Object.entries(filteredForm.formSubsections).forEach(([subsectionKey, subsection]) => {
    if (subsection.formSubsectionTitle === "Women's Health") {
      // Only keep this subsection if patient is female
      if (!isPatientFemale(patientFormData)) {
        delete filteredForm.formSubsections[subsectionKey];
      }
    }
  });
  
  return filteredForm;
};

/**
 * Gets export data in a structured format ready for backend processing
 * @param {Object} patientDataModel - The patient's data model
 * @param {Object} informantData - Informant data
 * @param {Object} selectedCategories - Which categories to export
 * @returns {Object} - Structured export data
 */
export const getExportData = (patientDataModel, informantData, selectedCategories) => {
  const exportData = {
    patient: {
      id: patientDataModel.patientId,
      name: `${patientDataModel.firstName} ${patientDataModel.lastName}`,
      email: patientDataModel.email,
      organisation: patientDataModel.organisation
    },
    categories: {}
  };

  const analysis = analyzePatientDataForExport(patientDataModel, informantData);

  // Add selected categories
  if (selectedCategories.patientHistory && analysis.patientHistory.hasData) {
    exportData.categories.patientHistory = {
      forms: analysis.patientHistory.forms.filter(form => form.hasResponses),
      formData: {}
    };
    
    // Add actual form data with gender filtering
    analysis.patientHistory.forms.forEach(form => {
      if (form.hasResponses && patientDataModel.formData[form.key]) {
        // Apply subsection filtering for forms that contain Women's Health
        const filteredForm = filterFormSubsectionsForExport(
          patientDataModel.formData[form.key], 
          patientDataModel.formData
        );
        exportData.categories.patientHistory.formData[form.key] = filteredForm;
      }
    });
  }

  if (selectedCategories.patientScales && analysis.patientScales.hasData) {
    exportData.categories.patientScales = {
      forms: analysis.patientScales.forms.filter(form => form.hasResponses),
      formData: {}
    };
    
    // Add actual form data
    analysis.patientScales.forms.forEach(form => {
      if (form.hasResponses && patientDataModel.formData[form.key]) {
        exportData.categories.patientScales.formData[form.key] = patientDataModel.formData[form.key];
      }
    });
  }

  if (selectedCategories.informantScales && analysis.informantScales.hasData) {
    exportData.categories.informantScales = {
      forms: analysis.informantScales.forms.filter(form => form.hasResponses),
      informantData: {}
    };
    
    // Add actual informant form data
    analysis.informantScales.forms.forEach(form => {
      if (form.hasResponses && informantData[form.informantId]?.formData[form.key]) {
        if (!exportData.categories.informantScales.informantData[form.informantId]) {
          exportData.categories.informantScales.informantData[form.informantId] = {
            informantInfo: {
              name: form.informantName,
              relationship: form.relationship,
              email: informantData[form.informantId].email
            },
            formData: {}
          };
        }
        exportData.categories.informantScales.informantData[form.informantId].formData[form.key] = 
          informantData[form.informantId].formData[form.key];
      }
    });
  }

  return exportData;
};

/**
 * Formats timestamp for display
 * @param {*} timestamp - Timestamp to format
 * @returns {string} - Formatted date string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown date';
  
  try {
    // Handle Firestore timestamp
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    
    // Handle regular Date object or string
    return new Date(timestamp).toLocaleString();
  } catch (e) {
    return 'Invalid date';
  }
};

/**
 * Generates a filename for the export
 * @param {string} patientName - Patient's full name
 * @param {string} format - Export format (pdf/excel)
 * @param {Array} categories - Selected categories
 * @returns {string} - Generated filename
 */
export const generateExportFilename = (patientName, format, categories) => {
  const cleanName = patientName.replace(/[^a-zA-Z0-9]/g, '_');
  const dateStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const categoryStr = categories.join('_');
  const extension = format === 'pdf' ? 'pdf' : 'xlsx';
  
  return `${cleanName}_${categoryStr}_${dateStr}.${extension}`;
};

/**
 * Export utility functions for backend use
 */
export { shouldIncludeFormInExport, filterFormSubsectionsForExport };
